/**
 * TagChip — renders a `Tag` in its palette colour.
 *
 * Tag colours resolve to the categorical `--viz-*` ramp, which is already tuned
 * per theme, so a tag keeps its identity when the theme flips. The fill and the
 * hairline are mixed from that one hue, which is why no per-colour token is
 * needed for eight palette entries.
 *
 * `muted` drops the hue entirely. Eight categorical colours are right on a
 * record detail, where a handful of chips carry meaning; they are wrong on a
 * board where three hundred of them compete with the one colour field that
 * matters. Dense surfaces pass `muted` and keep the ramp for the detail view.
 *
 * `TAG_TEXT_MIX` is the share of the hue kept before mixing in `--text-primary`.
 * The light theme binds: `--viz-7` amber at 10px needs the mix to clear 4.5:1.
 * `scripts/contrast-check.mjs` asserts every slot in both themes.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { X } from 'lucide-react';
import type { Tag, TagColor } from '@shared/types';
import { cn } from '@/lib/cn';
import { mixSurface, mixText } from './Badge';

/**
 * Palette → viz slot. Chosen for maximum separation between adjacent chips
 * rather than literal hue names: the ramp has one blue, so `sky` borrows the
 * cyan slot and `teal` the green one.
 */
export const TAG_HUE: Record<TagColor, string> = {
  blue: 'var(--viz-1)',
  gold: 'var(--viz-2)',
  sky: 'var(--viz-3)',
  violet: 'var(--viz-4)',
  rose: 'var(--viz-5)',
  teal: 'var(--viz-6)',
  amber: 'var(--viz-7)',
  slate: 'var(--viz-8)',
};

export const TAG_TEXT_MIX = 66;
export const TAG_FILL_MIX = 16;

/**
 * `lg` is the removable size: 24px is the WCAG 2.5.8 minimum target, so a chip
 * that carries a dismiss button has room to contain one rather than overhang it.
 */
const SIZE: Record<TagChipSize, string> = {
  // 11px is the floor for a chip label anywhere in the product: 10px is below
  // the smallest size the rest of the UI uses and costs contrast headroom.
  sm: 'h-4 gap-1 pl-1 text-[length:var(--text-xs)]',
  md: 'h-5 gap-1.5 pl-1.5 text-xs',
  lg: 'h-6 gap-1.5 pl-2 text-xs',
};

const PAD_END: Record<TagChipSize, string> = { sm: 'pr-1', md: 'pr-1.5', lg: 'pr-2' };

export type TagChipSize = 'sm' | 'md' | 'lg';

export interface TagChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  tag: Tag;
  /** Renders a dismiss affordance named "Remove {tag}". */
  onRemove?: (tag: Tag) => void;
  size?: TagChipSize;
  /** Append `tag.count` when the API supplied one. */
  showCount?: boolean;
  /** Drop the palette hue — for dense surfaces that already own a colour field. */
  muted?: boolean;
}

export const TagChip = forwardRef<HTMLSpanElement, TagChipProps>(function TagChip(
  { tag, onRemove, size = 'md', showCount = false, muted = false, className, style, ...rest },
  ref,
) {
  const hue = TAG_HUE[tag.color] ?? TAG_HUE.slate;
  const paint = muted
    ? {
        background: 'transparent',
        borderColor: 'var(--line-strong)',
        color: 'var(--text-secondary)',
      }
    : {
        background: mixSurface(hue, TAG_FILL_MIX),
        borderColor: mixSurface(hue, 30),
        color: mixText(hue, TAG_TEXT_MIX),
      };

  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center rounded-sm border font-medium whitespace-nowrap',
        'leading-none',
        SIZE[size],
        // `pr-1` cancels the button's `-mr-1`: the 24px target ends flush with
        // the chip edge instead of poking 2px past it.
        onRemove ? 'pr-1' : PAD_END[size],
        className,
      )}
      style={{ ...paint, ...style }}
      {...rest}
    >
      <span className="min-w-0 truncate">{tag.name}</span>

      {showCount && tag.count !== undefined ? (
        <span className="shrink-0 opacity-70 tnum">{tag.count}</span>
      ) : null}

      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${tag.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(tag);
          }}
          className={cn(
            // 24×24 hit area (WCAG 2.5.8) on a 12px glyph. The negative margins
            // keep the *chip* compact: on `lg` the button fills the 24px row
            // exactly, on the smaller sizes it overhangs symmetrically instead
            // of stretching the pill.
            '-my-1 -mr-1 ml-0.5 grid size-6 shrink-0 place-items-center opacity-60',
            'transition-[opacity,background-color] duration-[var(--dur-fast)] ease-[var(--ease-out)]',
            'hover:bg-hover hover:opacity-100 focus-visible:opacity-100',
          )}
          style={{ borderRadius: 'var(--radius-full)' }}
        >
          <X aria-hidden="true" className="size-3" strokeWidth={2.5} />
        </button>
      ) : null}
    </span>
  );
});
